import { NVLineChartDownsampled } from "common/graphs/DownSampling";
import Loading from "common/Loading";
import * as d3 from "d3";
import { useContext, useEffect, useState } from "react";
import { getMemoryStatsLines, parseStatsMemData } from "./api";
import useDebouncer from "common/hooks/debouncer";
import BqnContext from "common/BqnContext";
import styled from "styled-components";
import { HOURS_TO_PERIOD, LINES_BY_HOUR } from "common/constants";

const ChartContainer = styled.div`
  display: flex;
  height: 300px;

  &.big {
    height: 500px;
  }

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

function tooltipContentGenerator(totals) {
  return (d) => {
    const date = d3.timeFormat("%Y-%m-%d  %H:%M:%S")(new Date(d.value));
    let html = '<b style="white-space: pre;"> ' + date + "  </b><br/><table>";
    const ROW_MAX = 16;
    let value,
      rowIdx,
      rowCnt = d.series.length < ROW_MAX ? d.series.length : ROW_MAX,
      row = [];

    for (let i = 0; i < rowCnt; i++) {
      row[i] = "<tr>";
    }

    for (let i = 0; i < d.series.length; i++) {
      value = d.series[i].value;
      if (isNaN(value) || value === null) continue;
      rowIdx = i % ROW_MAX;
      row[rowIdx] +=
        '<td class="legend-color-guide"><div style="background-color:' +
        d.series[i].color +
        ';"></div></td>';
      row[rowIdx] +=
        "<td>" +
        d.series[i].key +
        '</td><td align="right"><b>' +
        d3.format(".1f")(value) +
        " " +
        "%" +
        "</b></td>";
      row[rowIdx] +=
        '<td align="right"><b>' +
        d3.format(".3f")((value * totals[i]) / 102400) +
        " GB" +
        "</b></td>";
    }

    for (let i = 0; i < rowCnt; i++) {
      row[i] += "</tr>";
      html += row[i];
    }
    html += "</table>";

    return html;
  };
}

// The data is indexed by BQN, so bqn is the BQN whose data is drawn, while
// bqnIdAsked is the BQN the statistics are asked of: the BQN selected in the
// header, which is no BQN in particular when the whole cluster is selected
// and only the BQN serving the GUI is drawn.
export const MemoryChart = ({
  totals,
  bqn,
  bqnIdAsked,
  range,
  initialRange,
  hours,
  big,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ fields: [], items: [] });
  const debounce = useDebouncer(500 /*ms*/);
  const { clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;

  const { System, Mpool, Dpdk } = totals;
  const period = HOURS_TO_PERIOD[hours];

  const doLoad = () => {
    setLoading(true);
    setData({ fields: [], items: [] });
    if (System !== null && Mpool !== null) {
      getMemoryStatsLines(hours * LINES_BY_HOUR, mode, bqnIdAsked)
        .then((response) => {
          return parseStatsMemData(response, period, {
            System: System[bqn],
            Mpool: Mpool[bqn],
            Dpdk: Dpdk[bqn],
          });
        })
        .then((response) => {
          setData(response);
          setLoading(false);
        })
        .catch((error) =>
          console.warn(
            `Memory statistics lines retrieval failed: ${
              !error.message ? "unknown reason" : error.message
            }`,
          ),
        );
    }
  };

  useEffect(() => {
    debounce(() => doLoad());
  }, [totals]);

  const { fields, items } = data;
  const selectedRange = range || initialRange;

  return (
    <ChartContainer className={big ? "big" : ""}>
      {loading ? <Loading /> : null}
      {fields.length !== 0 && !loading ? (
        <NVLineChartDownsampled
          xField="time"
          items={items}
          range={selectedRange}
          yAxisUnits={"%"}
          xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
          fields={fields.map((field) => {
            return { name: field, label: field };
          })}
          yAxisTopGap={0.05}
          yAxisTicksNumber={5}
          xAxisTicksNumber={5}
          tooltipContentGenerator={tooltipContentGenerator([
            System[bqn],
            Mpool[bqn],
            ...Dpdk[bqn],
          ])}
          fixedYRange={[0, 1]}
        />
      ) : null}
    </ChartContainer>
  );
};
